import React, { useEffect, useRef, useState } from 'react';
import { useDevice } from '../../../../lib/hooks/device';
import PureTable from '../../../common/PureTable/PureTable';
import { classes } from '../../../../lib/utils/cssUtils';
import { Label } from '../../../common/Label/Label';
import Button from '../../../common/Button/Button';
import { TABLE_LABELS, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import WithDevice from '../../../common/WithDevice/WithDevice';
import { ConfirmDialog } from '../../../common/ConfirmDialog/ConfirmDialog';
import { ScormInlineActionProps } from './DownloadsList.types';
import styles from './DownloadsList.module.scss';
import { pushRemoveTryEvent } from '../../../../lib/utils/analytics/analyticsScormHelper';

const ScormInlineAction = ({ id, name, type, labelKeys, onConfirm, isRemoving }: ScormInlineActionProps) => {
  const [isPendingConfirm, setIsPendingConfirm] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const device = useDevice();

  const handleDeleteClick = () => {
    setIsPendingConfirm(true);
    pushRemoveTryEvent(name, type);
  };

  useEffect(() => {
    const focusListener = (event: FocusEvent) => {
      event.stopPropagation();

      if (!containerRef.current?.contains(event.relatedTarget as Node)) {
        setIsPendingConfirm?.(false);
      }
    };

    if (device === 'desktop' && isPendingConfirm) {
      confirmRef.current?.focus();
      containerRef.current?.addEventListener('focusout', focusListener);
    }

    return () => containerRef.current?.removeEventListener('focusout', focusListener);
  }, [device, isPendingConfirm]);

  return (
    <>
      <WithDevice mobile tablet>
        <PureTable.DeleteAction
          id={id}
          titleKey="delete.downloadsListItem"
          onClick={handleDeleteClick}
          className={styles.inlineMobileAction}
        />
      </WithDevice>
      <div
        ref={containerRef}
        className={classes(styles.inlineActionContainer, styles.actionButtonMarker, {
          [styles.hide]: device !== 'desktop',
        })}
      >
        {isRemoving && <PureTable.Loader className={styles.inlineActionLoader} />}
        <div className={classes({ [styles.hide]: isRemoving })}>
          {isPendingConfirm ? (
            <div className={styles.actionsContainer}>
              <Label name={labelKeys.content} className={styles.inlineActionMessage} />
              <Button
                ref={confirmRef}
                onClick={() => onConfirm({ id, trainingName: name, trainingType: type })}
                className={styles.inlineActionButton}
              >
                <Label name={labelKeys.confirmButton} />
              </Button>
              <Button
                variant="outlined"
                onClick={() => setIsPendingConfirm(false)}
                className={styles.inlineActionButton}
              >
                <Label name={COMMON_LABELS.DIALOG.CANCEL} />
              </Button>
            </div>
          ) : (
            <Button variant="text" onClick={handleDeleteClick} className={styles.inlineActionDelete}>
              <Label name={TABLE_LABELS.ACTION('delete.downloadsListItem')} />
            </Button>
          )}
        </div>
        <WithDevice mobile tablet>
          {isPendingConfirm && (
            <ConfirmDialog
              onCancel={() => setIsPendingConfirm(false)}
              onConfirm={() => onConfirm({ id, trainingName: name, trainingType: type })}
              labelKeys={labelKeys}
              isOpen
            />
          )}
        </WithDevice>
      </div>
    </>
  );
};

export default ScormInlineAction;
